import React, { useState, useEffect } from 'react'
import axios from 'axios'

const API_URL = 'https://jupiter.grub.digital/'

const TYPE_CONFIG = {
  credito: { label: 'Crédito', color: '#10b981', bg: '#ecfdf5' },
  debito: { label: 'Débito', color: '#ef4444', bg: '#fef2f2' },
  expiracao: { label: 'Expiração', color: '#6b7280', bg: '#f3f4f6' },
  ajuste: { label: 'Ajuste', color: '#f59e0b', bg: '#fffbeb' },
  bonificacao: { label: 'Bonificação', color: '#8b5cf6', bg: '#f5f3ff' }
}

const styles = {
  container: {
    padding: '0'
  },
  filterRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap'
  },
  select: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '0.85rem',
    color: '#374151',
    backgroundColor: '#fff',
    cursor: 'pointer',
    outline: 'none'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  entry: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '10px'
  },
  entryLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1
  },
  badge: (type) => ({
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '6px',
    fontSize: '0.7rem',
    fontWeight: 600,
    backgroundColor: (TYPE_CONFIG[type] || TYPE_CONFIG.credito).bg,
    color: (TYPE_CONFIG[type] || TYPE_CONFIG.credito).color
  }),
  entryInfo: {
    flex: 1
  },
  entryDescription: {
    fontSize: '0.85rem',
    color: '#111827',
    fontWeight: 500
  },
  entryDate: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    marginTop: '2px'
  },
  entryPoints: (tipo) => ({
    fontSize: '0.95rem',
    fontWeight: 700,
    color: tipo === 'credito' || tipo === 'bonificacao' ? '#10b981' : '#ef4444',
    textAlign: 'right',
    minWidth: '80px'
  }),
  entrySaldo: {
    fontSize: '0.7rem',
    color: '#9ca3af',
    textAlign: 'right'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginTop: '20px'
  },
  pageButton: (disabled) => ({
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    backgroundColor: disabled ? '#f9fafb' : '#fff',
    color: disabled ? '#d1d5db' : '#374151',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer'
  }),
  pageInfo: {
    fontSize: '0.8rem',
    color: '#6b7280'
  },
  empty: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280'
  },
  loading: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280'
  }
}

function LoyaltyStatement({ brand, token }) {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filterType, setFilterType] = useState('')

  useEffect(() => {
    if (brand && token) {
      fetchExtrato()
    }
  }, [brand, token, page, filterType])

  const fetchExtrato = async () => {
    setLoading(true)
    try {
      const params = { page, limit: 20 }
      if (filterType) params.tipo = filterType

      const response = await axios.get(`${API_URL}public/fidelidade/extrato/${brand}`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      })
      if (response.data && response.data.success) {
        setEntries(response.data.data || [])
        setTotalPages(response.data.pagination?.pages || 1)
      }
    } catch (err) {
      // Silently fail
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatPoints = (pontos, tipo) => {
    const value = Math.abs(pontos)
    const prefix = tipo === 'credito' || tipo === 'bonificacao' ? '+' : '-'
    return `${prefix}${value.toLocaleString('pt-BR')}`
  }

  if (loading && entries.length === 0) {
    return <div style={styles.loading}>Carregando extrato...</div>
  }

  return (
    <div style={styles.container}>
      <div style={styles.filterRow}>
        <select
          style={styles.select}
          value={filterType}
          onChange={(e) => { setFilterType(e.target.value); setPage(1) }}
        >
          <option value="">Todos os tipos</option>
          <option value="credito">Crédito</option>
          <option value="debito">Débito</option>
          <option value="expiracao">Expiração</option>
          <option value="ajuste">Ajuste</option>
          <option value="bonificacao">Bonificação</option>
        </select>
      </div>

      {entries.length === 0 ? (
        <div style={styles.empty}>
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📋</div>
          <div style={{ fontWeight: 600 }}>Nenhuma movimentação encontrada</div>
        </div>
      ) : (
        <div style={styles.list}>
          {entries.map((entry) => (
            <div key={entry.id} style={styles.entry}>
              <div style={styles.entryLeft}>
                <div style={styles.badge(entry.tipo)}>
                  {(TYPE_CONFIG[entry.tipo] || TYPE_CONFIG.credito).label}
                </div>
                <div style={styles.entryInfo}>
                  <div style={styles.entryDescription}>{entry.descricao || '-'}</div>
                  <div style={styles.entryDate}>{formatDate(entry.created_at)}</div>
                </div>
              </div>
              <div>
                <div style={styles.entryPoints(entry.tipo)}>
                  {formatPoints(entry.pontos, entry.tipo)}
                </div>
                <div style={styles.entrySaldo}>
                  Saldo: {entry.saldo_apos?.toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            style={styles.pageButton(page <= 1)}
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            ← Anterior
          </button>
          <span style={styles.pageInfo}>
            Página {page} de {totalPages}
          </span>
          <button
            style={styles.pageButton(page >= totalPages)}
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Próxima →
          </button>
        </div>
      )}
    </div>
  )
}

export default LoyaltyStatement
